<template>
  <div class="free-ship-progress mt-sm">
    <p class="free-ship-progress__msg" aria-live="polite">
      <template v-if="!nextTarget">
        🎉 <b>{{ lastTier.reward }} unlocked!</b>
      </template>
      <template v-else>
        Add <b>{{ formatShort(nextTarget.min - subTotal) }}</b> more →
        <b>{{ nextTarget.reward }}</b>
      </template>
    </p>

    <div class="free-ship-progress__bar">
      <div class="free-ship-progress__track">
        <div
          class="free-ship-progress__fill"
          :style="{ width: `${fillPercent}%` }"
        ></div>
      </div>
      <ol class="free-ship-progress__tiers">
        <li
          v-for="(tier, index) in tiers"
          :key="tier.reward"
          class="free-ship-progress__tier"
          :class="{
            'is-passed': index < level - 1,
            'is-current': index === level - 1,
            'is-next': index === level,
            'is-pop': index === popIndex,
          }"
        >
          <span v-if="tier.tag" class="free-ship-progress__tag">{{
            tier.tag
          }}</span>
          <span
            class="free-ship-progress__node"
            :class="{ 'free-ship-progress__node--trophy': tier.trophy }"
            aria-hidden="true"
          >
            <svg class="free-ship-progress__ic-off" viewBox="0 0 24 24">
              <path :d="tier.icon"></path>
            </svg>
            <svg
              v-if="tier.trophy"
              class="free-ship-progress__ic-on"
              viewBox="0 0 24 24"
            >
              <defs>
                <linearGradient id="fspGold" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0" stop-color="#FFF6C2"></stop>
                  <stop offset=".45" stop-color="#F6C744"></stop>
                  <stop offset="1" stop-color="#C98A12"></stop>
                </linearGradient>
              </defs>
              <path
                d="M7 6H4.3a2.6 2.6 0 0 0 3 4M17 6h2.7a2.6 2.6 0 0 1-3 4"
                fill="none"
                stroke="#B57A0C"
                stroke-width="1.7"
              ></path>
              <path
                d="M6.5 3.3h11V8a5.5 5.5 0 0 1-11 0V3.3Z"
                fill="url(#fspGold)"
                stroke="#94600A"
                stroke-width=".9"
              ></path>
              <path
                d="M10.8 13.3h2.4v2.9h-2.4z"
                fill="#D99A1E"
                stroke="none"
              ></path>
              <path
                d="M7.8 20.7h8.4l-.9-4.6H8.7z"
                fill="url(#fspGold)"
                stroke="#94600A"
                stroke-width=".9"
              ></path>
              <path
                d="M12 5.4l.8 1.6 1.8.2-1.3 1.2.4 1.8-1.7-.9-1.7.9.4-1.8-1.3-1.2 1.8-.2z"
                fill="#fff"
                stroke="none"
              ></path>
              <path
                d="M8.4 4.6v3.3"
                stroke="#fff"
                stroke-width="1"
                stroke-linecap="round"
                opacity=".75"
                fill="none"
              ></path>
            </svg>
            <span class="free-ship-progress__badge">
              <svg class="free-ship-progress__b-lock" viewBox="0 0 24 24">
                <rect x="5" y="11" width="14" height="10" rx="2"></rect>
                <path d="M8 11V8a4 4 0 0 1 8 0v3"></path>
              </svg>
              <svg class="free-ship-progress__b-check" viewBox="0 0 24 24">
                <path d="M5 12.5l4.5 4.5L19 7.5"></path>
              </svg>
            </span>
          </span>
          <span class="free-ship-progress__reward">{{ tier.reward }}</span>
          <span class="free-ship-progress__status">{{
            tierStatus(tier, index)
          }}</span>
        </li>
      </ol>
    </div>
    <p class="free-ship-progress__note">*Free shipping on most orders</p>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import useCartStore from '@/store/cart'
import { formatPrice } from '@/utils/product'
import {
  SHIP_OFF_THRESHOLD,
  FREE_SHIP_THRESHOLD,
} from '@/utils/constants/discount'

interface Tier {
  items: number
  min: number
  reward: string
  icon: string
  trophy?: boolean
  tag?: string
}

const POP_DURATION = 700
// fill stops: empty, node1, node2, node3, full
const POS = [0, 1 / 6, 1 / 2, 5 / 6, 1]

const cartStore = useCartStore()

const subTotal = computed(() => Number(cartStore.subTotal) || 0)
const itemCount = computed(() => cartStore.totalCartItems || 0)

const formatShort = (value: number) => formatPrice(value).replace(/\.00$/, '')

const tiers = computed<Tier[]>(() => [
  {
    items: 1,
    min: 0,
    reward: 'FREE Upsize',
    icon: 'M3 4h2l2.4 11h10.2L20 7H6M9 19.5h.01M17 19.5h.01',
  },
  {
    items: 2,
    min: SHIP_OFF_THRESHOLD,
    reward: 'Up to 50% off Ship',
    icon: 'M2 6h12v10H2zM14 9h4l3 3.5V16h-7zM6.5 19a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Zm11 0a1.8 1.8 0 1 0 0-3.6 1.8 1.8 0 0 0 0 3.6Z',
  },
  {
    items: 3,
    min: FREE_SHIP_THRESHOLD,
    reward: 'FREE Ship*',
    icon: 'M7 4h10v4a5 5 0 0 1-10 0V4ZM7 6H4.5a2.5 2.5 0 0 0 2.8 3.8M17 6h2.5a2.5 2.5 0 0 1-2.8 3.8M12 13v3M8.5 20h7l-.8-4h-5.4z',
    trophy: true,
    tag: 'Best value',
  },
])
const lastTier = computed(() => tiers.value[tiers.value.length - 1])

// number of unlocked tiers (0..3)
const level = computed(() => {
  if (subTotal.value >= FREE_SHIP_THRESHOLD) return 3
  if (subTotal.value >= SHIP_OFF_THRESHOLD) return 2
  return itemCount.value >= 1 ? 1 : 0
})

// the next reward that still needs more $ to unlock
const nextTarget = computed(() =>
  tiers.value.find((tier) => tier.min > subTotal.value)
)

const tierStatus = (tier: Tier, index: number) => {
  if (index < level.value - 1) return 'Upgraded'
  if (index === level.value - 1) return '✓ Unlocked'
  return tier.min
    ? `${tier.items} items · ${formatShort(tier.min)}`
    : `${tier.items} item`
}

// jump to the reached node; inside a segment, fill by $
const fillPercent = computed(() => {
  const next = tiers.value[level.value]
  if (!next) return 100
  let partial = 0
  if (level.value > 0) {
    const lo = tiers.value[level.value - 1].min
    partial = Math.max(
      0,
      Math.min(0.9, (subTotal.value - lo) / (next.min - lo))
    )
  }
  const from = POS[level.value]
  return (from + partial * (POS[level.value + 1] - from)) * 100
})

// replay the "pop" animation on the tier that was just reached
const popIndex = ref(-1)
let popTimer: ReturnType<typeof setTimeout> | undefined
watch(level, (now, prev) => {
  if (now <= prev) return
  clearTimeout(popTimer)
  popIndex.value = now - 1
  popTimer = setTimeout(() => {
    popIndex.value = -1
  }, POP_DURATION)
})
</script>

<style lang="scss">
$b: '.free-ship-progress';

#{$b} {
  --rp-accent: #b5441f;
  --rp-accent-soft: #f6e3da;
  --rp-ink: #2b2522;
  --rp-muted: #8a817c;
  --rp-line: #ece6e1;
  --rp-bg: #fff;
  --rp-gold: #e3a821;
  --rp-gold-dk: #9a640a;
  font: inherit;
  color: var(--rp-ink);
  padding: 12px 12px 10px;
  border: 1px solid var(--rp-line);
  border-radius: 12px;
  background: var(--rp-bg);

  * {
    box-sizing: border-box;
  }

  &__msg {
    position: relative;
    z-index: 3;
    margin: 0;
    max-width: calc(66% - 6px);
    text-align: left;
    font-size: 13px;
    line-height: 1.35;

    b {
      color: var(--rp-accent);
    }
  }

  &__bar {
    position: relative;
    padding-top: 22px;
    margin-top: -18px;
  }

  &__note {
    margin: 8px 0 0;
    padding-top: 5px;
    border-top: 1px solid var(--rp-line);
    font-size: 9.5px;
    line-height: 1.3;
    color: var(--rp-muted);
    text-align: right;
  }

  &__track {
    position: absolute;
    left: 0;
    right: 0;
    top: 43px;
    height: 4px;
    border-radius: 4px;
    background: var(--rp-line);
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    width: 0;
    background: var(--rp-accent);
    border-radius: 4px;
    transition: width 0.5s cubic-bezier(0.3, 0.7, 0.2, 1);
  }

  &__tiers {
    position: relative;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    margin: 0;
    padding: 0;
    list-style: none;
  }

  &__tier {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 1px;
    min-width: 0;
  }

  &__node {
    position: relative;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--rp-bg);
    border: 2px solid var(--rp-line);
    color: var(--rp-muted);
    margin: 6px 0 10px;
    transition: background 0.3s, border-color 0.3s, color 0.3s;

    svg {
      width: 18px;
      height: 18px;
      fill: none;
      stroke: currentColor;
      stroke-width: 1.7;
      stroke-linecap: round;
      stroke-linejoin: round;
    }
  }

  &__status {
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: var(--rp-muted);
  }

  &__reward {
    font-size: 12.5px;
    font-weight: 700;
    line-height: 1.25;
    color: var(--rp-muted);
  }

  // next target
  &__tier.is-next &__node {
    border-color: var(--rp-accent);
    color: var(--rp-accent);

    &::after {
      content: '';
      position: absolute;
      inset: -6px;
      border-radius: 50%;
      border: 2px solid var(--rp-accent);
      opacity: 0;
      animation: free-ship-pulse 1.8s ease-out infinite;
    }
  }

  &__tier.is-next &__reward {
    color: var(--rp-ink);
  }

  // current reward
  &__tier.is-current &__node {
    background: var(--rp-accent);
    border-color: var(--rp-accent);
    color: #fff;
  }

  &__tier.is-current &__status {
    color: var(--rp-accent);
  }

  &__tier.is-current &__reward {
    color: var(--rp-ink);
  }

  // passed (replaced by bigger reward)
  &__tier.is-passed &__node {
    background: var(--rp-accent-soft);
    border-color: var(--rp-accent-soft);
    color: var(--rp-accent);
  }

  &__tier.is-passed &__reward {
    color: var(--rp-muted);
    text-decoration: line-through;
    text-decoration-thickness: 1px;
  }

  &__tier.is-pop &__node {
    animation: free-ship-pop 0.55s cubic-bezier(0.3, 1.6, 0.5, 1);
  }

  // lock / check badge
  &__badge {
    position: absolute;
    right: -5px;
    bottom: -4px;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: grid;
    place-items: center;
    background: var(--rp-bg);
    border: 1.5px solid var(--rp-line);
    color: var(--rp-muted);
  }

  &__node &__badge svg {
    width: 9px;
    height: 9px;
    stroke-width: 2.6;
  }

  &__b-check {
    display: none;
  }

  &__tier.is-current &__badge,
  &__tier.is-passed &__badge {
    background: #2e9e5b;
    border-color: var(--rp-bg);
    color: #fff;
  }

  &__tier.is-current &__b-lock,
  &__tier.is-passed &__b-lock {
    display: none;
  }

  &__tier.is-current &__b-check,
  &__tier.is-passed &__b-check {
    display: block;
  }

  // gold trophy tier
  &__node--trophy {
    width: 46px;
    height: 46px;
    margin: 0 0 4px;

    svg {
      width: 26px;
      height: 26px;
    }
  }

  &__ic-on {
    display: none;
  }

  &__tier.is-next &__node--trophy {
    border-color: var(--rp-gold);
    color: var(--rp-gold);

    &::after {
      border-color: var(--rp-gold);
    }
  }

  &__tier.is-current &__node--trophy {
    background: radial-gradient(circle at 35% 30%, #fffbe8, #ffe9a3 70%);
    border-color: var(--rp-gold);
    box-shadow: 0 0 0 4px rgba(227, 168, 33, 0.22),
      0 6px 18px rgba(227, 168, 33, 0.55);

    &::before {
      content: '';
      position: absolute;
      inset: -16px;
      border-radius: 50%;
      z-index: -1;
      background: repeating-conic-gradient(
        rgba(246, 199, 68, 0.55) 0 10deg,
        transparent 10deg 30deg
      );
      -webkit-mask: radial-gradient(circle, #000 30%, transparent 70%);
      mask: radial-gradient(circle, #000 30%, transparent 70%);
      animation: free-ship-spin 9s linear infinite;
    }
  }

  &__tier.is-current &__node--trophy &__ic-off {
    display: none;
  }

  &__tier.is-current &__node--trophy &__ic-on {
    display: block;
    width: 34px;
    height: 34px;
    position: relative;
    z-index: 1;
  }

  &__tier.is-current:has(#{$b}__node--trophy) &__status,
  &__tier.is-current:has(#{$b}__node--trophy) &__reward {
    color: var(--rp-gold-dk);
  }

  &__tier.is-pop &__node--trophy {
    animation: free-ship-pop-big 0.7s cubic-bezier(0.3, 1.6, 0.5, 1);
  }

  // best value tag with gold sheen
  &__tag {
    position: absolute;
    top: -20px;
    left: 50%;
    transform: translateX(-50%);
    white-space: nowrap;
    overflow: hidden;
    padding: 2px 8px 2px;
    border-radius: 999px;
    font-size: 9.5px;
    font-weight: 800;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: #4a2e00;
    background: linear-gradient(
      135deg,
      #b8860b,
      #f3cf63 35%,
      #fff3b8 50%,
      #f3cf63 65%,
      #b8860b
    );
    box-shadow: 0 1px 0 rgba(255, 255, 255, 0.6) inset,
      0 2px 6px rgba(184, 134, 11, 0.35);
    z-index: 2;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        105deg,
        transparent 30%,
        rgba(255, 255, 255, 0.95) 48%,
        transparent 66%
      );
      background-size: 250% 100%;
      background-position: 150% 0;
      animation: free-ship-sheen 2.6s ease-in-out infinite;
    }
  }
}

@media (max-width: 380px) {
  #{$b} {
    padding: 10px;

    &__reward {
      font-size: 11.5px;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  #{$b} {
    &__fill,
    &__node {
      transition: none;
    }

    &__tier.is-next &__node::after,
    &__tier.is-pop &__node,
    &__tier.is-current &__node--trophy::before,
    &__tag::after {
      animation: none;
    }
  }
}

@keyframes free-ship-pulse {
  0% {
    transform: scale(0.85);
    opacity: 0.55;
  }
  100% {
    transform: scale(1.35);
    opacity: 0;
  }
}

@keyframes free-ship-pop {
  0% {
    transform: scale(0.6);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes free-ship-pop-big {
  0% {
    transform: scale(0.4);
  }
  60% {
    transform: scale(1.18);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes free-ship-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes free-ship-sheen {
  0%,
  35% {
    background-position: 150% 0;
  }
  75%,
  100% {
    background-position: -60% 0;
  }
}
</style>
